import {FC, useCallback, useEffect, useState} from 'react';
import {Popover} from '@mantine/core';
import {useDropzone} from 'react-dropzone';
import clsx from 'clsx';

import {useSelector} from '~/store/hooks';
import Icon, {Icons} from '~/common/Icon';
import Button from '~/common/Button';
import useMediaQuery from '~/hooks/useMediaQuery';
import {isMobileDevice} from '~/utils/browserDetect';

import s from './styles.module.scss';
import {validateImageFile} from './utils';

interface SearchFormProps {
  form: any;
  onSubmit: any;
  photoSearch?: boolean;
  photoSearchBlob?: null;
}

const SearchForm: FC<SearchFormProps> = ({form, onSubmit, photoSearch, photoSearchBlob}) => {
  const imageUploadLimits = useSelector((store) => store.app.data.searchForm.imageUploadLimits);
  const [opened, setOpened] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const onDrop = useCallback(
    async (acceptedFiles: any[]) => {
      const file = acceptedFiles[0];
      const errorMessage = await validateImageFile(file, imageUploadLimits);

      if (errorMessage) {
        form.setError('file', {type: 'custom', message: errorMessage});
      } else {
        form.setValue('file', file);
        setOpened(false);
      }
    },
    [form]
  );

  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/webp': [],
    },
  });
  const {query, file} = form.watch();

  // on photo search results: convert previously uploaded image (blob) to file, only once!
  useEffect(() => {
    if (photoSearchBlob && !file) {
      const decodedData = window.atob(photoSearchBlob);
      const uInt8Array = new Uint8Array(decodedData.length);
      for (let i = 0; i < decodedData.length; ++i) {
        uInt8Array[i] = decodedData.charCodeAt(i);
      }
      const blobPart = new Blob([uInt8Array]);

      const fileBlob = new File([blobPart], 'upload.jpg', {
        type: 'image/jpeg',
        lastModified: new Date().getTime(),
      });

      form.setValue('file', fileBlob);
    }
  }, []);

  useEffect(() => {
    URL.revokeObjectURL(imageUrl || '');

    if (!file) {
      setImageUrl(null);
      return;
    }

    setImageUrl(URL.createObjectURL(file));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  return (
    <form
      id="search-photo-form"
      className={s['search-form']}
      onSubmit={form.handleSubmit(onSubmit)}
      action="/search-photo/gallery"
      method="post"
    >
      <div className={s['search-form__box']}>
        {file && imageUrl && (
          <div className={s['search-form__image-preview']}>
            <img alt="upload preview" src={imageUrl} />
            <span className={s['search-by']}>Search by uploaded image</span>
            <div
              className={s['search-form__clear-btn']}
              onClick={() => {
                form.setFocus('query');
                form.setValue('file', null);
              }}
            >
              <Icon name={Icons.clear} />
            </div>
          </div>
        )}
        {!file && (
          <input
            {...form.register('query')}
            id="search-input"
            className={s['search-form__input']}
            maxLength={2048}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck="false"
          />
        )}
        {!file && query.length > 0 && (
          <div
            className={s['search-form__clear-btn']}
            onClick={() => {
              form.setValue('query', '');
              form.setFocus('query');
            }}
          >
            <Icon name={Icons.clear} />
          </div>
        )}
        {photoSearch && (
          <Popover opened={opened} position="bottom-end" withArrow onChange={setOpened}>
            <Popover.Target>
              <div
                className={s['search-form__photo-search-btn']}
                onClick={() => setOpened((v) => !v)}
              >
                <Icon name={Icons.photo} />
              </div>
            </Popover.Target>

            <Popover.Dropdown
              className={clsx(s['search-form__upload-form'], {
                [s['search-form__upload-form--fixed']]: isLandscape && isMobileDevice(),
              })}
            >
              <div className={s['']}>
                <div className={s['search-form__upload-title']}>
                  Upload a photo where the face is visible.
                </div>
                <div className={s['search-form__upload-subtitle']}>
                  to show similar people based on the uploaded photo
                </div>
                <div className={s['search-form__dropzone']} {...getRootProps()}>
                  <input {...getInputProps()} name="file" />
                  <div className={s['search-form__dropzone-content']}>
                    Please upload a clear and well-lit photo where the person&apos;s face is
                    visible.
                  </div>
                  {!isTouchDevice && (
                    <>
                      <div className={s['search-form__dropzone-title']}>
                        <Icon name={Icons.dragndrop} />
                        <span>Drag and drop an image here</span>
                      </div>
                      <div className={s['search-form__dropzone-divider']}>or</div>
                    </>
                  )}
                  <Button variant="outline" className={s['search-form__dropzone-button']}>
                    <Icon
                      name={Icons.searchPhoto}
                      className={s['search-form__dropzone-button-icon']}
                    />
                    <span>Select a file</span>
                  </Button>
                  {form.formState.errors.file?.message && (
                    <div className={s['search-form__dropzone-error']}>
                      {form.formState.errors.file?.message}
                    </div>
                  )}
                </div>
              </div>
            </Popover.Dropdown>
          </Popover>
        )}
        <Icon
          className={s['search-form__search-icon']}
          onClick={() => form.handleSubmit(onSubmit)()}
          name={Icons.search}
          width={21}
          height={21}
        />
      </div>
    </form>
  );
};

export default SearchForm;
